import { el } from '../lib/dom.js';
import { listTimezones, browserTimezone, timezoneLabel } from '../lib/timezone.js';

// ---------------------------------------------------------------------------
// "Send immediately" / "Schedule invitation" control, shared by org-new.js
// (the owner invite) and org-detail.js's add-member form (a member invite) so
// the two cannot drift apart on this UI.
// ---------------------------------------------------------------------------

let uid = 0;

/**
 * @returns {{
 *   root: HTMLElement,
 *   setEnabled: (enabled: boolean) => void,
 *   reset: () => void,
 *   getValue: () => { inviteMode: 'IMMEDIATE'|'SCHEDULED', inviteDate?: string, inviteTime?: string, timezone?: string },
 * }}
 */
/**
 * @param {{getPreferredTimezone?: () => string | undefined}} [opts] Called
 *   whenever "Schedule invitation" is selected, to default the timezone
 *   field to the organisation's own trial timezone (when the caller has one
 *   to offer — e.g. org-new.js's trial-schedule section) rather than always
 *   falling back to the browser's. The operator can still freely pick a
 *   different invitation timezone afterwards — this only sets the starting
 *   point, once, at the moment scheduling is turned on.
 */
export function inviteScheduleControl(opts = {}) {
  const { getPreferredTimezone } = opts;
  const prefix = `invite-sched-${(uid += 1)}`;

  const modeImmediate = el('input', {
    type: 'radio',
    name: `${prefix}-mode`,
    id: `${prefix}-immediate`,
    checked: true,
  });
  const modeScheduled = el('input', { type: 'radio', name: `${prefix}-mode`, id: `${prefix}-scheduled` });

  const dateInput = el('input', { class: 'input mono', type: 'date', id: `${prefix}-date` });
  const timeInput = el('input', { class: 'input mono', type: 'time', id: `${prefix}-time` });
  const tzSelect = el(
    'select',
    { class: 'input', id: `${prefix}-timezone` },
    listTimezones().map((tz) => el('option', { value: tz, selected: tz === browserTimezone() }, timezoneLabel(tz))),
  );

  const scheduleFields = el(
    'div',
    { class: 'field-grid schedule-fields', hidden: true },
    el('div', { class: 'field' }, el('label', { for: `${prefix}-date` }, 'Date'), dateInput),
    el('div', { class: 'field' }, el('label', { for: `${prefix}-time` }, 'Time'), timeInput),
    el('div', { class: 'field' }, el('label', { for: `${prefix}-timezone` }, 'Timezone'), tzSelect),
  );

  function syncVisibility() {
    scheduleFields.hidden = !modeScheduled.checked;
    if (modeScheduled.checked && typeof getPreferredTimezone === 'function') {
      const preferred = getPreferredTimezone();
      if (preferred) tzSelect.value = preferred;
    }
  }
  modeImmediate.addEventListener('change', syncVisibility);
  modeScheduled.addEventListener('change', syncVisibility);

  // .seg/.seg-opt is the existing two-way segmented-control component
  // (styles.css) — an exact fit for this binary choice, no new CSS needed.
  const root = el(
    'div',
    { class: 'invite-schedule' },
    el(
      'div',
      { class: 'seg' },
      el('label', { class: 'seg-opt' }, modeImmediate, 'Send immediately'),
      el('label', { class: 'seg-opt' }, modeScheduled, 'Schedule invitation'),
    ),
    scheduleFields,
  );

  return {
    root,
    /** Hide/show the whole control — call from the "Send the invite email" checkbox's change handler. */
    setEnabled(enabled) {
      root.hidden = !enabled;
    },
    reset() {
      modeImmediate.checked = true;
      dateInput.value = '';
      timeInput.value = '';
      syncVisibility();
    },
    getValue() {
      return {
        inviteMode: modeScheduled.checked ? 'SCHEDULED' : 'IMMEDIATE',
        inviteDate: dateInput.value || undefined,
        inviteTime: timeInput.value || undefined,
        timezone: tzSelect.value || undefined,
      };
    },
  };
}
